import React from 'react';
import classNames from 'classnames';

import { PaneLayoutInteractionType } from './constants';

import styles from './CollapsiblePaneLayout.module.scss';

interface CollapsiblePaneLayoutProps {
    children: React.ReactNode;
    isPaneOpen: boolean;
    onPaneClickOutside?: (e: React.MouseEvent) => void;
    pane?: React.ReactNode;
    paneInteraction?: string;
    paneAriaHidden?: boolean;
    customPaneClassname?: string;
    customPaneContentClassname?: string;
    customWrapperClassname?: string;
    offsetBottom?: number;
}

interface CollapsiblePaneLayoutState {
    offsetBottom: number;
}

export class CollapsiblePaneLayout extends React.PureComponent<
    CollapsiblePaneLayoutProps,
    CollapsiblePaneLayoutState
> {
    private paneContent: React.RefObject<HTMLDivElement>;

    static defaultProps = {
        paneInteraction: PaneLayoutInteractionType.asCollapsible,
        offsetBottom: 0,
    };

    constructor(props: CollapsiblePaneLayoutProps) {
        super(props);
        this.paneContent = React.createRef();
        this.state = { offsetBottom: props.offsetBottom || 0 };
    }

    handlePaneClick(e) {
        if (e.target.contains(this.paneContent.current)) {
            this.props.onPaneClickOutside?.(e);
        }
    }

    componentDidUpdate() {
        this.setState({
            offsetBottom: this.props.offsetBottom || 0,
        });
    }

    render() {
        const {
            children,
            customWrapperClassname,
            customPaneClassname,
            customPaneContentClassname,
            isPaneOpen,
            pane,
            paneAriaHidden,
            paneInteraction,
        } = this.props;

        const paneClasses = classNames(
            styles.edsCollapsiblePaneLayoutPane,
            {
                [styles.edsCollapsiblePaneLayoutPaneOpen as string]: isPaneOpen,
                [styles.edsCollapsiblePaneLayoutPaneAsModal as string]:
                    paneInteraction === PaneLayoutInteractionType.asModal,
            },
            customPaneClassname,
        );

        const paneContentClasses = classNames(
            styles.edsCollapsiblePaneLayoutPaneContent,
            {
                [styles.edsCollapsiblePaneLayoutPaneContentOffset as string]:
                    !!this.props.offsetBottom &&
                    paneInteraction === PaneLayoutInteractionType.asModal,
            },
            customPaneContentClassname,
        );

        const paneWrapper = pane ? (
            <div
                className={paneClasses}
                data-spec="eds-collapsible-pane-layout-pane"
                onClick={this.handlePaneClick.bind(this)}
            >
                <div
                    className={paneContentClasses}
                    ref={this.paneContent}
                    style={{ bottom: this.state?.offsetBottom }}
                >
                    {pane}
                </div>
            </div>
        ) : null;

        const contentClasses = classNames(styles.edsCollapsiblePaneLayoutContent, {
            [styles.edsCollapsiblePaneLayoutContentHasPane as string]: pane,
        });

        return (
            <div
                className={classNames(styles.edsCollapsiblePaneLayout, customWrapperClassname)}
                aria-hidden={paneAriaHidden}
            >
                <div className={contentClasses}>{children}</div>
                {paneWrapper}
            </div>
        );
    }
}
